import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";

import { useGlobalContext } from "../../ContextApi/GlobalContext";
import { loginCompany } from "../../Backend/authFunction";

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Min 6 characters")
    .required("Password is required"),
});

const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { dispatch } = useGlobalContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });
  
  const onSubmit = async (data) => {
    setLoader(true);
    try {
      const result = await loginCompany(data);
      if (result.success) {
        dispatch({
          authToken: result.data.token,
          companyData: result.data,
        });

        // Close modal and show success
        setLoader(false);
        onClose();
        reset(); // Clear form

        Swal.fire({
          icon: "success",
          title: "Welcome back!",
          text: "Login successful",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/dashboard");
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setLoader(false);
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.message || "Invalid credentials",
        confirmButtonColor: "#E63946",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6">
      {/* 1. Backdrop (Dark Overlay) */}
      <div
        className="absolute inset-0 bg-[#0B1C2D]/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* 2. Modal Content */}
      <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all animate-fade-in-up border border-[#E2E8F0]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-[#64748B] hover:text-[#0B1C2D] transition-colors p-2 rounded-full hover:bg-slate-50"
        >
          <span className="material-symbols-outlined">close</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E63946]/10 text-[#E63946] mb-4">
            <span className="material-symbols-outlined text-2xl">lock</span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-[#0B1C2D]">
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-[#64748B]">
            Enter your details to access your dashboard.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium leading-6 text-[#0B1C2D] mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                {...register("email")}
                className={`block w-full rounded-lg border-0 py-3 pl-4 text-[#0B1C2D] shadow-sm ring-1 ring-inset ${
                  errors.email
                    ? "ring-red-300 focus:ring-red-500"
                    : "ring-[#E2E8F0] focus:ring-[#E63946]"
                } placeholder:text-[#94A3B8] focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-all`}
                placeholder="name@company.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium leading-6 text-[#0B1C2D]">
                Password
              </label>
              <a
                href="/forgot-password"
                className="text-xs font-semibold text-[#E63946] hover:text-[#be123c] hover:underline"
              >
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password")}
                className={`block w-full rounded-lg border-0 py-3 pl-4 pr-10 text-[#0B1C2D] shadow-sm ring-1 ring-inset ${
                  errors.password
                    ? "ring-red-300 focus:ring-red-500"
                    : "ring-[#E2E8F0] focus:ring-[#E63946]"
                } placeholder:text-[#94A3B8] focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-all`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0B1C2D]"
              >
                <span className="material-symbols-outlined text-[20px]">
                  {showPassword ? "visibility_off" : "visibility"}
                </span>
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-500">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loader}
            className="w-full flex justify-center items-center gap-2 rounded-lg bg-[#E63946] px-8 py-3 text-base font-semibold text-white shadow-md shadow-[#E63946]/20 hover:bg-[#be123c] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loader ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#64748B]">
            Don't have an account?{" "}
            <Link
              to="/register"
              onClick={onClose}
              className="font-semibold text-[#E63946] hover:text-[#be123c] transition-colors"
            >
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;